import { useEffect, useState, useCallback } from "react";
import {
  getAdminChatRooms,
  getChatRoomMessages,
  deleteChatMessage,
} from "../../services/ingroove/adminService";
import { formatDate, formatDateTime } from "../../utils/formatters";
import PageHeader from "../../components/common/PageHeader";
import Avatar from "../../components/common/Avatar";
import {
  Search,
  ChevronLeft,
  ChevronRight,
  MessageSquare,
  ArrowLeft,
  Trash2,
  Calendar,
} from "lucide-react";

const ChatRooms = () => {
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [meta, setMeta] = useState({ totalRecords: 0, totalPages: 0 });

  // Selected room state
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [messages, setMessages] = useState([]);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const fetchRooms = useCallback(async (searchVal, pageVal) => {
    setLoading(true);
    try {
      const res = await getAdminChatRooms({
        search: searchVal,
        pageNumber: pageVal,
        pageSize: 20,
      });
      setRooms(res.data);
      setMeta({ totalRecords: res.totalRecords, totalPages: res.totalPages });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setPage(1);
      fetchRooms(search, 1);
    }, 400);
    return () => clearTimeout(timeout);
  }, [search]);

  useEffect(() => {
    fetchRooms(search, page);
  }, [page]);

  const openRoom = async (room) => {
    setSelectedRoom(room);
    setMessagesLoading(true);
    try {
      const msgs = await getChatRoomMessages(room.id);
      setMessages(msgs);
    } finally {
      setMessagesLoading(false);
    }
  };

  const handleDeleteMessage = async (messageId) => {
    setDeletingId(messageId);
    try {
      const ok = await deleteChatMessage(messageId);
      if (ok) setMessages((prev) => prev.filter((m) => m.id !== messageId));
    } finally {
      setDeletingId(null);
    }
  };

  // ── Messages view ─────────────────────────────────────────────
  if (selectedRoom)
    return (
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center gap-3">
          <button
            onClick={() => {
              setSelectedRoom(null);
              setMessages([]);
            }}
            className="flex items-center gap-1.5 text-xs text-text-muted hover:text-primary transition"
          >
            <ArrowLeft size={14} /> Back
          </button>
          <div className="h-4 w-px bg-border" />
          <div className="flex items-center gap-2">
            {selectedRoom.eventImage ? (
              <img
                src={selectedRoom.eventImage}
                alt=""
                className="w-6 h-6 rounded-md object-cover"
              />
            ) : (
              <div className="w-6 h-6 rounded-md bg-bg-elevated flex items-center justify-center">
                <Calendar size={12} className="text-text-muted" />
              </div>
            )}
            <div>
              <p className="text-sm font-semibold text-text-primary">
                {selectedRoom.eventTitle}
              </p>
              <p className="text-xs text-text-muted">{selectedRoom.hubName}</p>
            </div>
          </div>
          <span className="ml-auto text-xs text-text-muted">
            {messages.length} message{messages.length !== 1 ? "s" : ""}
          </span>
        </div>

        {/* Messages */}
        <div className="bg-bg-surface border border-border rounded-xl overflow-hidden">
          {messagesLoading ? (
            <div className="space-y-4 p-5">
              {Array.from({ length: 5 }).map((_, i) => (
                <div key={i} className="flex items-start gap-3 animate-pulse">
                  <div className="w-8 h-8 rounded-full bg-bg-elevated flex-shrink-0" />
                  <div className="flex-1 space-y-2">
                    <div className="h-3 bg-bg-elevated rounded w-24" />
                    <div className="h-4 bg-bg-elevated rounded w-64" />
                  </div>
                </div>
              ))}
            </div>
          ) : messages.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16">
              <MessageSquare size={28} className="text-border-hover mb-2" />
              <p className="text-xs text-text-muted">
                No messages in this room
              </p>
            </div>
          ) : (
            <div className="divide-y divide-border">
              {messages.map((msg) => (
                <div
                  key={msg.id}
                  className="flex items-start gap-3 px-5 py-4 hover:bg-bg-elevated transition group"
                >
                  <Avatar
                    name={msg.senderName?.split(" ")[0]}
                    surname={msg.senderName?.split(" ")[1]}
                    image={msg.senderImage}
                    size="sm"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <p className="text-xs font-semibold text-text-primary">
                        {msg.senderName}
                      </p>
                      <p className="text-xs text-text-hint">
                        {formatDateTime(msg.sentAt)}
                      </p>
                    </div>
                    <p className="text-sm text-text-secondary leading-relaxed break-words">
                      {msg.message}
                    </p>
                  </div>
                  <button
                    onClick={() => handleDeleteMessage(msg.id)}
                    disabled={deletingId === msg.id}
                    className="opacity-0 group-hover:opacity-100 p-1.5 rounded-lg text-text-muted hover:text-red-400 hover:bg-red-400/10 transition disabled:opacity-30"
                  >
                    <Trash2 size={13} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );

  // ── Rooms list view ───────────────────────────────────────────
  return (
    <div className="space-y-6">
      <PageHeader
        title="Chat Rooms"
        subtitle={`${meta.totalRecords.toLocaleString()} event chat rooms`}
      />

      {/* Search */}
      <div className="relative max-w-sm">
        <Search
          size={14}
          className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted"
        />
        <input
          type="text"
          placeholder="Search by event or hub..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full pl-9 pr-4 py-2 text-sm bg-bg-surface border border-border rounded-lg text-text-primary placeholder-text-hint focus:outline-none focus:border-primary transition"
        />
      </div>

      {/* Table */}
      <div className="bg-bg-surface border border-border rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-bg-elevated text-text-muted text-xs uppercase">
            <tr>
              <th className="px-5 py-3 text-left">Event</th>
              <th className="px-5 py-3 text-left">Hub</th>
              <th className="px-5 py-3 text-left">Messages</th>
              <th className="px-5 py-3 text-left">Last Activity</th>
              <th className="px-5 py-3 text-left">Created</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {loading ? (
              Array.from({ length: 6 }).map((_, i) => (
                <tr key={i} className="animate-pulse">
                  {Array.from({ length: 5 }).map((_, j) => (
                    <td key={j} className="px-5 py-3">
                      <div className="h-4 bg-bg-elevated rounded w-24" />
                    </td>
                  ))}
                </tr>
              ))
            ) : rooms.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-5 py-12 text-center">
                  <MessageSquare
                    size={24}
                    className="text-border-hover mx-auto mb-2"
                  />
                  <p className="text-xs text-text-muted">No chat rooms found</p>
                </td>
              </tr>
            ) : (
              rooms.map((room) => (
                <tr
                  key={room.id}
                  className="hover:bg-bg-elevated transition cursor-pointer"
                  onClick={() => openRoom(room)}
                >
                  {/* Event */}
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-3">
                      {room.eventImage ? (
                        <img
                          src={room.eventImage}
                          alt=""
                          className="w-9 h-9 rounded-lg object-cover flex-shrink-0"
                        />
                      ) : (
                        <div className="w-9 h-9 rounded-lg bg-bg-elevated flex items-center justify-center flex-shrink-0">
                          <Calendar size={14} className="text-text-muted" />
                        </div>
                      )}
                      <p className="text-text-primary font-medium truncate max-w-[180px]">
                        {room.eventTitle}
                      </p>
                    </div>
                  </td>

                  {/* Hub */}
                  <td className="px-5 py-3 text-text-secondary text-sm">
                    {room.hubName}
                  </td>

                  {/* Messages count */}
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-1.5 text-sm">
                      <MessageSquare size={13} className="text-primary" />
                      <span className="text-text-primary font-medium">
                        {room.totalMessages}
                      </span>
                    </div>
                  </td>

                  {/* Last activity */}
                  <td className="px-5 py-3 text-text-muted text-xs">
                    {room.lastMessageAt
                      ? formatDateTime(room.lastMessageAt)
                      : "—"}
                  </td>

                  {/* Created */}
                  <td className="px-5 py-3 text-text-muted text-xs">
                    {formatDate(room.createdAt)}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {meta.totalPages > 1 && (
        <div className="flex items-center justify-between text-xs text-text-muted">
          <span>
            Page {page} of {meta.totalPages} —{" "}
            {meta.totalRecords.toLocaleString()} rooms
          </span>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              className="p-1.5 rounded-lg border border-border hover:border-primary hover:text-primary transition disabled:opacity-30 disabled:cursor-not-allowed"
            >
              <ChevronLeft size={14} />
            </button>
            <button
              onClick={() => setPage((p) => Math.min(meta.totalPages, p + 1))}
              disabled={page === meta.totalPages}
              className="p-1.5 rounded-lg border border-border hover:border-primary hover:text-primary transition disabled:opacity-30 disabled:cursor-not-allowed"
            >
              <ChevronRight size={14} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChatRooms;
